import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [founder, setFounder] = useState(null);

  const loadFounder = () => {
    axios.get(`${API}/founder/status`).then((r) => setFounder(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadFounder();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setDone(true);
      toast.success("You're in the pack.", { description: "First access when the drop lands." });
      setEmail("");
      loadFounder();
    } catch (err) {
      toast.error("That email didn't take. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="founder" data-testid="newsletter" className="relative bg-[#050505] py-28 md:py-40 overflow-hidden concrete">
      <img
        src="/generated/wolf_white.png"
        alt=""
        aria-hidden="true"
        className="absolute -right-24 top-1/2 -translate-y-1/2 w-[560px] max-w-[70vw] object-contain opacity-[0.035] pointer-events-none"
      />
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="col-span-12 lg:col-span-7"
        >
          <span className="font-sans text-[11px] tracking-[0.45em] text-white/40">FOUNDER'S ACCESS</span>
          <h2 className="mt-6 font-display font-black uppercase tracking-normal leading-[0.9] text-white text-5xl md:text-7xl">
            Join the pack
            <br />
            before the drop.
          </h2>
          <p className="mt-6 font-sans text-base text-white/55 max-w-md leading-relaxed">
            500 numbered Founder's Edition pieces. Members get first access, private pricing and the story before anyone else.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="col-span-12 lg:col-span-5"
        >
          <form onSubmit={submit} className="flex flex-col gap-4" data-testid="newsletter-form">
            <div className="flex items-center border border-white/20 focus-within:border-white/60 transition-colors bg-black/40 backdrop-blur-sm">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (done) setDone(false); }}
                placeholder="your@email.com"
                className="flex-1 bg-transparent px-5 py-4 font-sans text-sm text-white placeholder:text-white/30 outline-none"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={loading}
                className="group flex items-center gap-2 h-full px-6 py-4 bg-white text-black font-sans text-xs tracking-[0.2em] disabled:opacity-60"
              >
                {loading ? "..." : done ? "JOINED" : "JOIN"}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <p className="font-sans text-[11px] tracking-[0.1em] text-white/30">
              No spam. No followers. Just the drop.
            </p>

            {founder && (
              <div data-testid="founder-scarcity" className="mt-4 border-t border-white/10 pt-5">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-sans text-[10px] tracking-[0.3em] text-white/45">FOUNDER'S EDITION</span>
                  <span className="font-sans text-[11px] tracking-[0.15em] text-white/70">
                    <span data-testid="founder-claimed" className="text-white font-medium">{founder.claimed}</span>
                    <span className="text-white/40"> / {founder.total} claimed</span>
                  </span>
                </div>
                <div className="relative h-1.5 w-full bg-white/10 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${founder.percent}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                    className="absolute inset-y-0 left-0 bg-white"
                  />
                </div>
                <p className="mt-2.5 font-sans text-[11px] tracking-[0.1em] text-white/40">
                  {founder.soldOut ? (
                    <span data-testid="founder-soldout" className="text-white/80">Founder's Edition claimed in full — join for the next drop.</span>
                  ) : (
                    <>Only <span className="text-white/80">{founder.remaining}</span> founder spots remain.</>
                  )}
                </p>
              </div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
